import { loadImportYaml } from './safeYaml';
import { detectImportFormat, formatLabel, isBruRequestText } from './detectFormat';
import { parseApiboo } from './parseApiboo';
import { parsePostmanCollection } from './parsePostman';
import { parseInsomniaExport } from './parseInsomnia';
import { parseOpenApi3, parseSwagger2 } from './parseOpenApi';
import { parseHar } from './parseHar';
import { parseBruno, parseBrunoYmlRequest } from './parseBruno';
import { parsePostmanEnv } from './parsePostmanEnv';
import { brunoJsonToIR } from './bruno/fromBrunoJson';
import { openCollectionToIR } from './bruno/fromYaml';
import { irToNormalized, secretEnvWarning } from './bruno/toNormalized';
import { parseHoppscotch } from './hoppscotch/parseHoppscotch';
import { parseHoppscotchEnv } from './hoppscotch/parseHoppscotchEnv';

/** Shown for a FILE that belongs to a Bruno collection folder (bruno.json, folder.bru, opencollection.yml…). */
export const BRUNO_PART_MESSAGE =
  'This is part of a Bruno collection folder. Import the whole folder (drop it here or Choose folder), or export it from Bruno as one file.';

export const UNRECOGNIZED_MESSAGE =
  'Unrecognized format. Supported: Apiboo Collection, Postman Collection v2, Insomnia v4 export, OpenAPI 3.x, Swagger 2.0, HAR, Bruno (.bru or .yml request, collection JSON or OpenCollection YAML export, or a collection folder), Hoppscotch collection or environment (JSON).';

function parseTextToData(text, filename) {
  const trimmed = text.trim();
  if (!trimmed) {
    throw new Error('File is empty');
  }

  const lower = (filename || '').toLowerCase();

  // Bruno .bru files are plain text — return as-is for detectImportFormat
  if (lower.endsWith('.bru') || /^\s*meta\s*\{/.test(trimmed)) {
    return trimmed;
  }

  const isYaml = lower.endsWith('.yaml') || lower.endsWith('.yml');

  if (isYaml) {
    return loadImportYaml(trimmed);
  }

  try {
    return JSON.parse(trimmed);
  } catch {
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      throw new Error('Invalid JSON');
    }
    return loadImportYaml(trimmed);
  }
}

function fallbackNameFromFilename(filename) {
  if (!filename) return 'Imported Collection';
  return filename.replace(/\.(json|ya?ml|har|bru)$/i, '') || 'Imported Collection';
}

/**
 * @param {string} text Raw file contents
 * @param {string} [filename] Original filename for format hints
 * @returns {{ kind: 'collection', format: string, formatLabel: string, collections: import('./types').NormalizedCollection[], environments?: import('./types').ImportedEnv[], warnings: string[] }
 *         | { kind: 'environment', format: string, formatLabel: string, environment: import('./types').ImportedEnv, environments: import('./types').ImportedEnv[], warnings: string[] }}
 */
export function parseImportFile(text, filename = '') {
  const warnings = [];
  const data = parseTextToData(text, filename);
  const format = detectImportFormat(data);

  if (format === 'postman_env' || format === 'hoppscotch_env') {
    // Environments are not collections: return early with a tagged result so
    // the modal can branch. The collection pipeline below (filtering, request
    // counting, "no collections found") has nothing meaningful to say here.
    const base = (filename || '').replace(/\.(json|ya?ml|har|bru)$/i, '');
    const environments = format === 'postman_env' ? [parsePostmanEnv(data, base)] : parseHoppscotchEnv(data, base);
    if (!environments.length) throw new Error('No environments found in file');
    secretEnvWarning(environments, warnings);
    return {
      kind: 'environment',
      format,
      formatLabel: formatLabel(format),
      environment: environments[0],
      environments,
      warnings,
    };
  }

  if (format === 'bruno_part' || (format === 'bruno' && !isBruRequestText(data))) {
    throw new Error(BRUNO_PART_MESSAGE);
  }

  if (format === 'unknown') {
    throw new Error(UNRECOGNIZED_MESSAGE);
  }

  let collections = [];
  let environments = [];

  switch (format) {
    case 'apiboo':
      // Throws its own, user-readable errors for a missing version or a major
      // this build does not speak — they must surface VERBATIM, not be wrapped
      // into the generic "unrecognized format" one.
      collections = parseApiboo(data);
      break;
    case 'postman':
      collections = parsePostmanCollection(data, warnings);
      break;
    case 'insomnia':
      collections = parseInsomniaExport(data, warnings);
      break;
    case 'openapi3':
      collections = parseOpenApi3(data, fallbackNameFromFilename(filename), warnings);
      break;
    case 'swagger2':
      collections = parseSwagger2(data, fallbackNameFromFilename(filename), warnings);
      break;
    case 'har':
      collections = parseHar(data, fallbackNameFromFilename(filename), warnings);
      break;
    case 'bruno':
      collections = parseBruno(data, fallbackNameFromFilename(filename), warnings);
      break;
    case 'bruno_yml':
      collections = parseBrunoYmlRequest(data, fallbackNameFromFilename(filename), warnings);
      break;
    case 'bruno_json':
    case 'opencollection': {
      const ir = format === 'bruno_json' ? brunoJsonToIR(data) : openCollectionToIR(data, fallbackNameFromFilename(filename));
      collections = [irToNormalized(ir, warnings)];
      environments = ir.environments;
      break;
    }
    case 'hoppscotch':
      collections = parseHoppscotch(data, warnings);
      break;
    default:
      throw new Error('Unsupported import format');
  }

  collections = collections.filter((c) => c && c.name);

  if (collections.length === 0) {
    throw new Error('No collections or requests found in file');
  }

  const totalRequests = collections.reduce((n, c) => n + (c.requests?.length || 0), 0);
  if (totalRequests === 0) {
    warnings.push('No HTTP requests were found; empty collections will still be created.');
  }

  return withEnvironments({
    kind: 'collection',
    format,
    formatLabel: formatLabel(format),
    collections,
    warnings,
  }, environments);
}

/** Environments that came WITH a collection (Bruno exports/folders): optional field, O4. */
export function withEnvironments(result, environments) {
  const envs = (environments || []).filter((e) => e && e.name);
  if (!envs.length) return result;
  secretEnvWarning(envs, result.warnings);
  return { ...result, environments: envs };
}

export { detectImportFormat, formatLabel } from './detectFormat';
export { parseImportFolder } from './folder/parseImportFolder';
